import { FC, useContext } from 'react';

import { css } from '@emotion/react';
import { ToucanComponents, ToucanColors } from '@jointoucan/toucan-design';
import { useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';

import Information from '~/components/shared/Dashboard/Layout/Information';
import Section from '~/components/shared/Dashboard/Layout/Section';
import { SettingTypes } from '~/constants/dashboardTypes';
import ModalContext from '~/context/ModalContext';
import { useLoggedInUser } from '~/hooks/useLoggedInUser';
import { useMobileDeviceUserAgent } from '~/hooks/useMobileDeviceUserAgent';

import SignUpInformation from '../SignUpInformation';

import EditAccountInfo from './EditAccountInfo';

const { Typography } = ToucanComponents;

interface AccountInformationSectionProps {
  onLoginSignup: () => void;
  setIsEditAccountInfoForMobileShown: (value: boolean) => void;
  setEditFieldType: (value: string) => void;
}

const AccountInformationSection: FC<AccountInformationSectionProps> = ({
  onLoginSignup,
  setIsEditAccountInfoForMobileShown,
  setEditFieldType,
}) => {
  const theme = useTheme();
  const { loggedInUser, loggedInAsRegistered } = useLoggedInUser();
  const { modal } = useContext(ModalContext);
  const isMobileUserAgent = useMobileDeviceUserAgent();
  const { t } = useTranslation('dashboard');

  const showEditAccountInfoForWeb = (editType: string) => {
    modal.showModal({
      ContentComponent: EditAccountInfo,
      disableBackdropClick: false,
      isCloseButtonVisible: true,
      contentProps: {
        editType,
        onClose: () => modal.closeModal(),
      },
    });
  };

  const onEditClick = (editType: string) => {
    if (isMobileUserAgent) {
      setEditFieldType(editType);
      setIsEditAccountInfoForMobileShown(true);
    } else {
      showEditAccountInfoForWeb(editType);
    }
  };

  return (
    <Section
      title={t('dashboardAccountInformation')}
      css={css`
        margin-bottom: ${theme.spacing(4)};
      `}
    >
      {!loggedInAsRegistered ? (
        <SignUpInformation onLoginSignup={onLoginSignup} externalAuthRedirectPath="/dashboard/settings" />
      ) : (
        <>
          <Information
            header={t('email')}
            information={loggedInUser && loggedInUser.email ? loggedInUser.email : ''}
            extraContent={
              loggedInUser &&
              loggedInUser.isExternallyAuthed && (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <img
                    src="/images/dashboard/google.svg"
                    width="12px"
                    height="12px"
                    alt={t('sharedGoogleLogo')}
                    css={css`
                      margin-right: ${theme.spacing(1)};
                    `}
                  />
                  <Typography variant="xs" isBold color={ToucanColors.gray[300]}>
                    {t('googleSignOn')}
                  </Typography>
                </div>
              )
            }
          />
          <Information
            header={t('name')}
            information={loggedInUser && loggedInUser.name ? loggedInUser.name : ''}
            buttonText={t('editName')}
            onClickButton={() => onEditClick(SettingTypes.name)}
            css={css`
              margin-top: ${theme.spacing(2)};
            `}
          />
          {loggedInUser && !loggedInUser.isExternallyAuthed && (
            <Information
              header={t('password')}
              information="••••••••"
              buttonText={t('editPassword')}
              onClickButton={() => onEditClick(SettingTypes.password)}
              css={css`
                margin-top: ${theme.spacing(2)};
              `}
            />
          )}
        </>
      )}
    </Section>
  );
};

export default AccountInformationSection;
